
import { debugMode, USE_FUSE_ONLY, ENABLE_FUZZY_FALLBACK, SEARCH_LIMIT } from './filter-logic-config.js';
import { scheduleFuzzyFallback, cancelScheduledFallback, performFuzzyFallback } from './fuzzy-fallback.js';
import { updateFuseIcon } from './fuse-icon.js';

const FALLBACK_OPTIONS = { maxFilesForFallback: 200000 };

/**
 * Schedule (or cancel) the fuzzy fallback path. Currently disabled via ENABLE_FUZZY_FALLBACK.
 * If feature flag is enabled and main search returned 0 results, schedules a 700ms-debounced fallback;
 * otherwise cancels any scheduled fallback (e.g. when main search now has results).
 * @returns {{ output: Array, fuseWasUsed: boolean }}
 */
export function handleFuzzyFallback({ output, query, files, filterManager, fuseWasUsed }) {
    const shouldRunFallback = ENABLE_FUZZY_FALLBACK
        && output.length === 0
        && query
        && query.trim()
        && !filterManager.isRegexSearch();

    if (shouldRunFallback) {
        return scheduleOrRunFallback({ output, query, files, fuseWasUsed });
    }

    cancelFallbackWithReason(output, query);
    return { output, fuseWasUsed };
}

function scheduleOrRunFallback({ output, query, files, fuseWasUsed }) {
    const isSearchPaused = window.searchStateManager && window.searchStateManager._paused === true;

    if (isSearchPaused) {
        if (debugMode === '1') {
            console.log('[FilterManager] Fuzzy fallback skipped: search paused');
        }
        return { output, fuseWasUsed };
    }

    if (typeof scheduleFuzzyFallback === 'function') {
        scheduleFuzzyFallback(query, files, FALLBACK_OPTIONS, debugMode);
        if (debugMode === '1') {
            console.log('[FilterManager] Fuzzy fallback scheduled with 700ms debounce:', {
                query,
                willExecuteIn: '700ms if no new input',
                note: 'Fallback will be cancelled if user continues typing'
            });
        }
        return { output, fuseWasUsed };
    }

    // scheduleFuzzyFallback unavailable - run direct (no debounce)
    if (debugMode === '1') {
        console.warn('[FilterManager] scheduleFuzzyFallback not available, using direct fallback');
    }
    const fallbackResults = performFuzzyFallback(query, files, FALLBACK_OPTIONS, debugMode);
    if (fallbackResults.length === 0) {
        return { output, fuseWasUsed };
    }

    let nextOutput = fallbackResults;
    if (nextOutput.length > SEARCH_LIMIT) {
        nextOutput = nextOutput.slice(0, SEARCH_LIMIT);
    }
    return { output: nextOutput, fuseWasUsed: USE_FUSE_ONLY ? true : fuseWasUsed };
}

function cancelFallbackWithReason(output, query) {
    const cancelReason = output.length > 0
        ? `regular search found ${output.length} results`
        : (!query || !query.trim() ? 'empty query' : 'unknown reason');
    cancelScheduledFallback(debugMode, cancelReason);

    if (debugMode === '1' && output.length > 0 && query && query.trim()) {
        console.log('[FilterManager] Cancelling scheduled fallback - regular search found results:', {
            query,
            resultsCount: output.length,
            note: 'Fallback no longer needed, regular search succeeded'
        });
    }
}

/**
 * Attach the fuzzyFallbackCompleted listener exactly once per page.
 * Listener applies fallback results to UI only if the user query hasn't changed
 * AND the regular search still has no results.
 */
export function attachFallbackListener(filterManager) {
    if (window.__fallbackEventListenerAttached) return;

    window.addEventListener('fuzzyFallbackCompleted', (event) => {
        const { query: fallbackQuery, results: fallbackResults } = event.detail;
        const currentQuery = (filterManager.filterElements?.searchInput?.value || '').trim();

        const hasRegularSearchResults = computeHasRegularSearchResults(currentQuery, fallbackQuery);

        if (currentQuery === fallbackQuery && fallbackResults.length > 0 && !hasRegularSearchResults) {
            applyFallbackToUI(fallbackResults, fallbackQuery);
        } else if (debugMode === '1') {
            const reason = currentQuery !== fallbackQuery
                ? 'query changed (user continued typing)'
                : hasRegularSearchResults
                    ? 'regular search found results'
                    : 'no fallback results';
            console.log('[FilterManager] Ignored fallback results:', {
                reason,
                fallbackQuery,
                currentQuery,
                hasRegularSearchResults,
                currentFilesCount,  // PRESERVED FROM ORIGINAL: undefined identifier (debug-only branch)
                fallbackResultsCount: fallbackResults.length
            });
        }
    });
    window.__fallbackEventListenerAttached = true;
}

function computeHasRegularSearchResults(currentQuery, fallbackQuery) {
    if (currentQuery !== fallbackQuery || !window.filterManager) return false;
    const currentFiles = window.lazyLoadManager?.allFiles || window.fileListManager?.files || [];
    return currentFiles.length > 0
        && currentQuery === fallbackQuery
        && !currentFiles.some(f => f._fromFallback === true);
}

function applyFallbackToUI(fallbackResults, fallbackQuery) {
    if (window.lazyLoadManager) {
        window.lazyLoadManager.setFiles(fallbackResults);
    } else if (window.fileListManager) {
        const limited = fallbackResults.slice(0, 30);
        window.fileListManager.updateFiles(limited);
    }

    if (window.updateResultsCount && typeof window.updateResultsCount === 'function') {
        window.updateResultsCount(fallbackResults.length);
    }

    updateFuseIcon(true);

    if (debugMode === '1') {
        console.log('[FilterManager] Applied fallback results:', {
            query: fallbackQuery,
            resultsCount: fallbackResults.length,
            note: 'Regular search still found no results, applying fallback'
        });
    }
}
